// src/features/files/components/SelectFileDialog/SelectFileDialog.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Alert,
  Typography,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  FolderOpen as FolderOpenIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useFolders } from '../../hooks/useFolders';
import { useFiles } from '../../hooks/useFiles';
import FileExplorer from '../FileExplorer';
import CreateFolderDialog from '../CreateFolderDialog';
import { getFolderName } from '../../utils/getFolderName';
import { useFilePicker, useUploadQueue, UploadProgressList } from '../../upload';

/**
 * SelectFileDialog - Reusable dialog for selecting files from the file browser
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether dialog is open
 * @param {Function} props.onClose - Callback when dialog closes
 * @param {Function} props.onSelectFile - Callback when file(s) selected
 *                                        Single mode: receives file object
 *                                        Multi mode: receives array of file IDs
 * @param {string} [props.startingFolderId] - Optional folder ID to start browsing from (default: root)
 * @param {Array} [props.initialBreadcrumbs] - Optional initial breadcrumb path (e.g., [{id: null, name: 'Root'}, {id: '123', name: 'Folder'}])
 * @param {string} [props.title] - Optional custom dialog title
 * @param {boolean} [props.multiSelect=false] - Enable multi-select mode with checkboxes
 * @param {string[]} [props.excludedFileIds=[]] - File IDs to exclude from selection (e.g., already linked files)
 * @param {boolean} [props.loading] - Optional loading state from parent
 * @param {string} [props.error] - Optional error message from parent
 * @param {boolean} [props.allowUpload=false] - Allow uploading (button and drag and drop) into the open folder.
 *   Uploaded files are selected straight away; in single mode the dialog then closes.
 * @param {string} [props.accept] - File types allowed for upload, like the file input's accept
 */
export default function SelectFileDialog({
  open,
  onClose,
  onSelectFile,
  startingFolderId = null,
  initialBreadcrumbs = null,
  title = 'files:select_file',
  multiSelect = false,
  excludedFileIds = [],
  loading: externalLoading = false,
  error: externalError = null,
  allowUpload = false,
  accept,
}) {
  const { t } = useTranslation(['common', 'files']);

  // Hooks
  const { loading: folderLoading, navigateToFolder, folderContents } = useFolders();
  const { uploadFile } = useFiles();

  // State
  const [currentFolderId, setCurrentFolderId] = useState(startingFolderId);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  // Files uploaded from this dialog, selected as soon as they're listed
  const [justUploaded, setJustUploaded] = useState([]);
  const currentFolderRef = useRef(startingFolderId);
  currentFolderRef.current = currentFolderId;

  const uploads = useUploadQueue({
    accept,
    upload: (file, { onUploadProgress, meta }) => uploadFile(file, {
      folder_id: meta.folderId,
      is_system_file: false,
      is_protected: true,
      onUploadProgress,
    }),
    onUploaded: (result) => {
      const fileId = result.data?.data?.file_id;
      if (fileId) setJustUploaded(prev => [...prev, fileId]);
    },
    // Reload once so the new files show up (and get selected)
    onFinished: (results) => { if (results.length) navigateToFolder(currentFolderRef.current); },
  });
  const addFiles = (files) => uploads.enqueue(multiSelect ? files : files.slice(0, 1), { folderId: currentFolderId });
  const picker = useFilePicker({ onFiles: addFiles, accept, multiple: multiSelect });

  // Initialize on dialog open
  useEffect(() => {
    if (!open) return;

    const initializeBrowser = async () => {
      const result = await navigateToFolder(startingFolderId);
      setCurrentFolderId(startingFolderId);

      // Initialize breadcrumbs
      if (initialBreadcrumbs) {
        // Use provided breadcrumbs if available
        setBreadcrumbs(initialBreadcrumbs);
      } else if (startingFolderId === null) {
        setBreadcrumbs([{ id: null, name: t('files:root') }]);
      } else if (result?.success && result?.data?.folder_name) {
        // If we have a folder name from the API, use it for the breadcrumb
        setBreadcrumbs([
          { id: null, name: t('files:root') },
          { id: startingFolderId, name: getFolderName(result.data.folder_name) },
        ]);
      }

      setSelectedFiles([]);
      setJustUploaded([]);
    };

    initializeBrowser();
  }, [open, startingFolderId, initialBreadcrumbs, navigateToFolder, t]);

  // Handle folder navigation
  const handleFolderClick = async (folder) => {
    const result = await navigateToFolder(folder.folder_id);

    if (result?.success) {
      setCurrentFolderId(folder.folder_id);

      // Build breadcrumb trail
      const newBreadcrumbs = [
        ...breadcrumbs,
        {
          id: folder.folder_id,
          name: getFolderName(folder.folder_name),
        },
      ];
      setBreadcrumbs(newBreadcrumbs);
    }
  };

  // Handle breadcrumb navigation
  const handleBreadcrumbClick = async (breadcrumb) => {
    await navigateToFolder(breadcrumb.id);
    setCurrentFolderId(breadcrumb.id);

    // Trim breadcrumbs to clicked position
    const index = breadcrumbs.findIndex((b) => b.id === breadcrumb.id);
    if (index !== -1) {
      setBreadcrumbs(breadcrumbs.slice(0, index + 1));
    }
  };

  // Handle selection change
  const handleSelectionChange = (files) => {
    setSelectedFiles(files);

    // In single select mode, immediately select and close
    if (!multiSelect && files.length > 0) {
      onSelectFile(files[0]);
      onClose();
    }
  };

  // Handle multi-select submit
  const handleSubmit = () => {
    if (multiSelect) {
      onSelectFile(selectedFiles.map((f) => f.file_id));
      onClose();
    }
  };

  // Handle folder created (CreateFolderDialog performs the API call itself)
  const handleFolderCreated = async () => {
    // Refresh current folder
    await navigateToFolder(currentFolderId);
  };

  // Get current contents
  const folders = folderContents?.data?.folders || folderContents?.data?.subfolders || [];
  const files = folderContents?.data?.files || [];
  const folderName = breadcrumbs[breadcrumbs.length - 1]?.name || t('files:root');

  const isLoading = folderLoading || externalLoading;
  const error = externalError || null;

  const emptyState = (
    <Box display="flex" flexDirection="column" alignItems="center" gap={1} py={2} color="text.secondary">
      <FolderOpenIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
      <Typography variant="body2">{t('files:select.empty')}</Typography>
      {allowUpload && (
        <>
          <Typography variant="body2">{t('files:upload.empty_hint')}</Typography>
          <Button variant="outlined" size="small" startIcon={<CloudUploadIcon />} onClick={picker.open} sx={{ mt: 1 }}>
            {t('files:upload.button')}
          </Button>
        </>
      )}
    </Box>
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>{t(title)}</DialogTitle>

        <DialogContent sx={{ minHeight: 500, p: 0, display: 'flex', flexDirection: 'column' }}>
          {error && (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          )}

          {isLoading && !folderContents ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight={400}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ height: 500 }}>
              <FileExplorer
                key={`file-explorer-${multiSelect ? 'multi' : 'single'}`}
                folders={folders}
                files={files}
                currentFolderId={currentFolderId}
                breadcrumbs={breadcrumbs}
                parentFolderName={folderContents?.data?.folder_name}
                layout="compact"
                selectionMode={multiSelect ? 'multi' : 'single'}
                excludedFileIds={excludedFileIds}
                autoSelectFileIds={justUploaded}
                onSelectionChange={handleSelectionChange}
                enablePreview={false}
                enableViewToggle={true}
                enableCompareMode={false}
                enableBreadcrumbs={true}
                enableCreateFolder={allowUpload}
                enableUploadFile={allowUpload}
                emptyState={emptyState}
                onFolderNavigate={handleFolderClick}
                onBreadcrumbClick={handleBreadcrumbClick}
                onCreateFolder={() => setCreateFolderOpen(true)}
                onUploadFile={picker.open}
                onFilesDropped={allowUpload ? addFiles : undefined}
                dropLabel={t('files:upload.drop_into', { folder: folderName })}
              />
            </Box>
          )}
          {picker.input}
          <UploadProgressList
            items={uploads.items}
            onDismiss={uploads.dismiss}
            sx={{ border: 0, borderTop: 1, borderTopColor: 'divider', borderRadius: 0 }}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={isLoading}>
            {t('common:buttons.cancel')}
          </Button>
          {multiSelect && (
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={selectedFiles.length === 0 || isLoading || uploads.busy}
            >
              {t('common:buttons.add')} ({selectedFiles.length})
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Create Folder Dialog */}
      <CreateFolderDialog
        open={createFolderOpen}
        onClose={() => setCreateFolderOpen(false)}
        parentFolderId={currentFolderId}
        onSuccess={handleFolderCreated}
      />
    </>
  );
}
